import { Link, useParams } from "wouter";
import { useCabinet } from "../lib/api.js";
import { StatusPill } from "../components/StatusPill.js";
import { SlotGrid } from "../components/SlotGrid.js";
import { SignalChart } from "../components/SignalChart.js";
import { CabinetActions } from "../components/CabinetActions.js";

function signalTone(signal: string | null) {
  if (!signal) return "muted" as const;
  const n = Number(signal);
  if (Number.isNaN(n)) return "muted" as const;
  if (n >= 19) return "ok" as const;
  if (n >= 10) return "warn" as const;
  return "bad" as const;
}

export function CabinetDetail() {
  const params = useParams<{ id: string }>();
  const id = params.id;
  const { data, isLoading, error } = useCabinet(id ?? null);

  if (!id) return null;

  return (
    <div className="mx-auto max-w-7xl px-6 py-8">
      <div className="mb-6 flex items-center gap-2 text-xs text-zinc-500">
        <Link href="/" className="hover:text-zinc-300">Fleet</Link>
        <span>/</span>
        <span className="font-mono text-zinc-300">{id}</span>
      </div>

      {isLoading && <div className="card p-10 text-center text-sm text-zinc-500">Loading…</div>}
      {error && (
        <div className="card p-6 text-sm text-rose-300">
          Failed to load: {(error as Error).message}
        </div>
      )}

      {data && (
        <>
          <header className="card flex flex-wrap items-start justify-between gap-4 p-5">
            <div>
              <div className="font-mono text-sm text-zinc-300">{data.cabinet.id}</div>
              <div className="mt-1 text-xs text-zinc-500">
                shop {data.cabinet.shopId ?? "—"} · type {data.cabinet.type ?? "—"}
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <StatusPill tone={data.cabinet.online ? "ok" : "bad"}>
                {data.cabinet.online ? "online" : "offline"}
              </StatusPill>
              <StatusPill tone={signalTone(data.cabinet.signal)}>
                signal {data.cabinet.signal ?? "—"} / 31
              </StatusPill>
            </div>
          </header>

          <div className="mt-6 space-y-6">
            <CabinetActions cabinet={data.cabinet} />
            <SlotGrid cabinet={data.cabinet} batteries={data.batteries} />
            <SignalChart cabinetId={id} hours={24} />
          </div>
        </>
      )}
    </div>
  );
}
